import { helper } from '@ember/component/helper';
import { htmlSafe } from '@ember/template';

const DANGEROUS_TAGS = new Set(['script', 'iframe', 'object', 'embed', 'base', 'form', 'meta', 'link']);
const DANGEROUS_ATTR = /^on/i;
// Script-executing URL schemes. Strip ALL C0 control chars + whitespace before
// testing: the DOM decodes entities in attribute values, so `java&#x09;script:`
// arrives as `java\tscript:` and browsers ignore the tab when resolving the
// scheme. A leading-`\s*` regex alone would miss those obfuscated forms.
const DANGEROUS_SCHEME = /^(?:javascript|vbscript):/i;
// Includes svg xlink:href, which also resolves URLs and bypassed the plain
// `href`/`src` set.
const URL_ATTRS = new Set(['href', 'src', 'action', 'formaction', 'data', 'xlink:href']);
function hasDangerousUrl(value) {
  const normalized = String(value).replace(/[\u0000-\u0020]+/g, '');
  return DANGEROUS_SCHEME.test(normalized);
}
const ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};
function sanitizeNode(node) {
  const children = [...node.childNodes];
  if (node.nodeType !== Node.ELEMENT_NODE) {
    children.forEach(sanitizeNode);
    return;
  }
  if (DANGEROUS_TAGS.has(node.tagName.toLowerCase())) {
    node.remove();
    return;
  }
  [...node.attributes].forEach(attr => {
    if (DANGEROUS_ATTR.test(attr.name)) {
      node.removeAttribute(attr.name);
    } else if (URL_ATTRS.has(attr.name) && hasDangerousUrl(attr.value)) {
      node.removeAttribute(attr.name);
    }
  });
  children.forEach(sanitizeNode);
}

/**
 * Sanitize an HTML string (plain-string variant).
 * Strips dangerous tags (script, iframe, object, embed, base, form, meta,
 * link), on* event-handler attributes and javascript: URLs.
 * Returns a plain string.
 */
function sanitizeHtmlString(html) {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    sanitizeNode(doc.body);
    return doc.body.innerHTML;
  } catch {
    return '';
  }
}

/**
 * Sanitize an HTML string and return it as an htmlSafe SafeString,
 * ready to render with {{...}} in templates or assign to tracked state.
 */
function sanitizeHtml(html) {
  return htmlSafe(sanitizeHtmlString(html));
}

/**
 * Escape plain text for safe interpolation into HTML.
 * Replaces & < > " ' with their entity equivalents. Returns a plain string.
 */
function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str).replace(/[&<>"']/g, ch => ESCAPE_MAP[ch]);
}
var safeString = helper(([string]) => sanitizeHtml(string));

export { safeString as default, escapeHtml, sanitizeHtml, sanitizeHtmlString };
//# sourceMappingURL=safe-string.js.map
